import type { Coords } from 'google-map-react';
import { t } from 'i18next';
import { makeAutoObservable } from 'mobx';

import { BIRequests } from '~/api/BI';
import type { AutocompleteSuggestion } from '~/api/DeliveryAddress';
import type { ETACalculation } from '~/api/ETA';
import type { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import { company } from '~/company/Company';
import type { DeliveryZoneRef } from '~/components/ModalDeliveryAddress/GMap/interfaces';
import { isInsideZone } from '~/components/ModalDeliveryAddress/GMap/utils';
import { mainStore, orderStore, userStore } from '~/stores';
import type { DeliveryAddress } from '~/stores/UserStore';

import type { EditAddressData } from './interface';

interface OutOfZoneInterest {
  coordinates: {
    lat: number;
    lng: number;
  };
  phone: string;
  email: string;
  name?: string;
  deviceId?: string;
  userId?: string;
}

export const {
  map: { center: mapCenter, zoom: mapZoom },
} = company.config;

class DeliveryAddressStore {
  isMapBusy = true;
  mapCenter: GeoCoordinates = mapCenter;
  selectedZone: Nullable<DeliveryZoneRef> = null;
  mapZoom = mapZoom;
  geolocationCoordinates: GeoCoordinates | null = null;
  curGeolocationAddress: AutocompleteSuggestion | null = null;
  isActiveGeolocation = false;
  isManuallyInput = false;
  isLoading = false;
  isAwaitGeolocation = false;
  isAwaitSubscription = false;
  inputAddressValue = '';
  inputAddressFocused = false;
  deliveryAddress: DeliveryAddress | null = null;
  etaCalculation: ETACalculation | null = null;
  isAddressNotExist = false;
  isAddressNotCovered = false;
  isSmallSizeCover = true;
  nameVal = '';
  emailVal = '';
  isShowAddressNotFound = false;
  isShowAddressFound = false;
  isPushRequested = false;
  editAddressData: EditAddressData = {
    addressId: null,
    address2: '',
    addressBld: '',
    addressFloor: '',
    addressApt: '',
    type: 'home',
    comment: '',
    instructions: [],
  };
  isChangeDeliveryAreaPopover = false;
  renderedZonesOnMap: Record<string, DeliveryZoneRef> = {};
  isOutOfZoneModalOpen = false;
  isShowOutOfZoneInline = false;
  outOfZonePhone = '';
  outOfZoneEmail = '';
  outOfZoneName = '';
  outOfZoneCoordinates: GeoCoordinates | null = null;

  private _geocodingAbortController: AbortController | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get isEmailValid(): boolean {
    return mainStore.validateEmail(this.emailVal);
  }

  get isFormValid(): boolean {
    return this.isEmailValid && !!this.nameVal.length;
  }

  setAddressType(type: string) {
    if (type === 'home') {
      this.setAddressTypeValue(type);
    } else {
      this.setAddressTypeValue('');
    }
  }

  setAddressTypeValue(val: string) {
    this.editAddressData.type = val;
  }

  setIsMapBusy(flag: boolean) {
    this.isMapBusy = flag;
  }

  setIsActiveGeolocation(flag: boolean) {
    this.isActiveGeolocation = flag;
  }

  setIsManuallyInput(flag: boolean) {
    this.isManuallyInput = flag;
  }

  setIsLoading(flag: boolean) {
    this.isLoading = flag;
  }

  setInputAddressFocused(flag: boolean) {
    this.inputAddressFocused = flag;
  }

  setIsAddressNotExist(flag: boolean) {
    this.isAddressNotExist = flag;
  }

  setIsAddressNotCovered(flag: boolean) {
    this.isAddressNotCovered = flag;
  }

  setMapCenter(val: GeoCoordinates) {
    this.mapCenter = val;
  }

  setSelectedZone(zone: Nullable<DeliveryZoneRef>) {
    this.selectedZone = zone;
  }

  selectZoneByCoordinates(coords: GeoCoordinates) {
    const zone = this.getSelectedPolygon(coords);
    this.setMapCenter(coords);
    this.setSelectedZone(zone);
  }

  setMapZoom(val: number) {
    this.mapZoom = val;
  }

  setInputAddressValue(val: string) {
    this.inputAddressValue = val;
  }

  setDeliveryAddress(val: DeliveryAddress | null) {
    if (!val) {
      this.deliveryAddress = val;
      return;
    }

    const editData = this.editAddressData;
    this.deliveryAddress = Object.assign(val, {
      ...editData,
      addressBld: editData.addressBld || val.addressBld,
    });
  }

  setDeliveryAddress2(val: string) {
    if (this.editAddressData) {
      this.editAddressData.address2 = val;
    }
  }

  setDeliveryAddressFloor(val: string) {
    if (this.editAddressData && val.length < 51) {
      this.editAddressData.addressFloor = val;
    }
  }

  setDeliveryAddressBld(val: string) {
    if (this.editAddressData && val.length < 51) {
      this.editAddressData.addressBld = val;
    }
  }

  setDeliveryAddressApt(val: string) {
    if (this.editAddressData && val.length < 51) {
      this.editAddressData.addressApt = val;
    }
  }

  setDeliveryAddressComment(val: string) {
    if (this.editAddressData && val.length < 128) {
      this.editAddressData.comment = val;
    }
  }

  addInstruction(val: string) {
    this.editAddressData.instructions.push(val);
  }

  deleteInstruction(val: string) {
    this.editAddressData.instructions =
      this.editAddressData.instructions.filter(
        (instruction) => instruction !== val,
      );
  }

  resetInstructionList() {
    this.editAddressData.instructions = [];
  }

  setEtaCalculation(val: ETACalculation | null) {
    this.etaCalculation = val;
  }

  setIsSmallSizeCover(flag: boolean) {
    this.isSmallSizeCover = flag;
  }

  setNameVal(val: string) {
    this.nameVal = val;
  }

  setEmailVal(val: string) {
    this.emailVal = val;
  }

  setIsShowAddressNotFound(flag: boolean) {
    this.isShowAddressNotFound = flag;
  }

  setIsShowAddressFound(flag: boolean) {
    this.isShowAddressFound = flag;
  }

  setIsAwaitGeolocation(flag: boolean) {
    this.isAwaitGeolocation = flag;
  }

  setIsAwaitSubscription(flag: boolean) {
    this.isAwaitSubscription = flag;
  }

  setGeolocationCoordinates(val: GeoCoordinates | null) {
    this.geolocationCoordinates = val;

    /**
     * Code below sets new center of map, together with "geolocationCoordinates"
     *
     * TODO: Not sure why this properties are different. Needs to remove one of them
     * */
    if (val) {
      this.mapCenter = val;
    }
  }

  setCurGeolocationAddress(val: AutocompleteSuggestion | null) {
    this.curGeolocationAddress = val;
  }

  setIsPushRequested(flag: boolean) {
    this.isPushRequested = flag;
  }

  setEditAddressData(val: EditAddressData) {
    this.editAddressData = val;
  }

  setIsChangeDeliveryAreaPopover(flag: boolean) {
    this.isChangeDeliveryAreaPopover = flag;
  }

  setIsOutOfZoneModalOpen(isOpen: boolean) {
    this.isOutOfZoneModalOpen = isOpen;
  }

  setIsShowOutOfZoneInline(isShow: boolean) {
    this.isShowOutOfZoneInline = isShow;
  }

  setOutOfZonePhone(phone: string) {
    this.outOfZonePhone = phone;
  }

  setOutOfZoneEmail(email: string) {
    this.outOfZoneEmail = email;
  }

  setOutOfZoneName(name: string) {
    this.outOfZoneName = name;
  }

  setOutOfZoneCoordinates(coordinates: GeoCoordinates | null) {
    this.outOfZoneCoordinates = coordinates;
  }

  resetOutOfZoneForm() {
    this.outOfZonePhone = '';
    this.outOfZoneEmail = '';
    this.outOfZoneName = '';
    this.outOfZoneCoordinates = null;
    this.setIsOutOfZoneModalOpen(false);
  }

  async submitOutOfZoneInterest() {
    if (!this.outOfZoneCoordinates) {
      return;
    }

    const interestData: OutOfZoneInterest = {
      coordinates: this.outOfZoneCoordinates,
      phone: this.outOfZonePhone,
      email: this.outOfZoneEmail,
      name: this.outOfZoneName || undefined,
      deviceId: userStore.deviceId,
      userId: userStore.personalData.id || undefined,
    };

    try {
      await BIRequests.sendAnalytics('out-of-zone-interest', interestData);
      mainStore.pushAlert('success', t('outOfZone:thankYou'));
      deliveryAddressStore.setIsOutOfZoneModalOpen(false);
      this.resetOutOfZoneForm();
    } catch {
      mainStore.pushAlert('error', t('errors:somethingWentWrong'));
    }
  }

  resetStore() {
    this._geocodingAbortController?.abort();
    this._geocodingAbortController = null;
    this.isMapBusy = true;
    this.mapCenter = mapCenter;
    this.geolocationCoordinates = null;
    this.isActiveGeolocation = false;
    this.isManuallyInput = false;
    this.isLoading = false;
    this.isAwaitGeolocation = false;
    this.isAwaitSubscription = false;
    this.inputAddressValue = '';
    this.inputAddressFocused = false;
    this.deliveryAddress = null;
    this.etaCalculation = null;
    this.isAddressNotExist = false;
    this.isAddressNotCovered = false;
    this.isSmallSizeCover = true;
    this.nameVal = '';
    this.emailVal = '';
    this.isShowAddressNotFound = false;
    this.isShowAddressFound = false;
    this.curGeolocationAddress = null;
    this.isPushRequested = false;
    this.selectedZone = null;
    this.editAddressData = {
      addressId: null,
      address2: '',
      addressFloor: '',
      addressBld: '',
      addressApt: '',
      type: 'home',
      comment: '',
      instructions: [],
    };
  }

  async geocoding(skipGeocoding = false, { lat, lng }: GeoCoordinates) {
    this._geocodingAbortController?.abort();
    const abortController = new AbortController();
    this._geocodingAbortController = abortController;

    this.setIsLoading(true);
    const deliveryMethod = this.getDeliveryMethodBySelectedZone();

    try {
      const etaCalculation = await orderStore
        .fetchETA(`${lat},${lng}`, deliveryMethod, {
          signal: abortController.signal,
        })
        .catch(() => null);

      if (abortController.signal.aborted) return;

      mainStore.sendToRN('sendTags', {
        not_in_the_area: 'false',
      });

      this.setEtaCalculation(etaCalculation);
      this.setIsAddressNotCovered(false);
      this.setIsAddressNotExist(false);

      if (!etaCalculation) {
        if (company.isEnabledOutOfZoneModal) {
          this.setOutOfZoneCoordinates({ lat, lng });
          this.setIsShowOutOfZoneInline(true);
        } else {
          mainStore.pushAlert('error', t('addressOutOfArea'));
        }

        mainStore.sendToRN('sendTags', {
          not_in_the_area: true,
        });

        this.setIsAddressNotCovered(true);
        this.setDeliveryAddress(null);
        this.setInputAddressValue('');
        return;
      }

      this.setIsShowOutOfZoneInline(false);

      mainStore.popAlert();

      if (!skipGeocoding) {
        const prevPlaceId = this.deliveryAddress?.placeId;
        try {
          const deliveryAddress = await userStore.geocodingByCoordinates({
            lat,
            lng,
          });

          if (abortController.signal.aborted) return;

          this.setDeliveryAddress(deliveryAddress);
          this.setInputAddressValue(deliveryAddress?.address1 || '');
        } catch {
          if (abortController.signal.aborted) return;
          this.setDeliveryAddress(null);
          this.setInputAddressValue('');
        }
        if (prevPlaceId === this.deliveryAddress?.placeId) {
          if (!this.etaCalculation && !userStore.isFirstLaunch) {
            this.setIsAddressNotCovered(true);
          }
          return;
        }
      }
    } catch {
      if (abortController.signal.aborted) return;
      this.setEtaCalculation(null);
      mainStore.sendToRN('sendTags', {
        not_in_the_area: true,
      });

      if (!userStore.isFirstLaunch) {
        this.setIsAddressNotCovered(true);
      }
    } finally {
      if (!abortController.signal.aborted) {
        this.setIsLoading(false);
      }
    }
  }

  getDeliveryMethodBySelectedZone(): ETADeliveryMethodType | undefined {
    if (!this.selectedZone) {
      return;
    }

    const { zone } = this.selectedZone;

    return zone.deliveryMethod.shippingType as ETADeliveryMethodType;
  }

  getSelectedPolygon(point: Coords) {
    const matchedZones: DeliveryZoneRef[] = [];

    for (const deliveryZoneRef of Object.values(this.renderedZonesOnMap)) {
      const isSelectedPolygon = isInsideZone(point, deliveryZoneRef.polygon);

      if (!isSelectedPolygon) {
        continue;
      }

      matchedZones.push(deliveryZoneRef);
    }

    if (!matchedZones.length) {
      return null;
    }

    const sorted = matchedZones.sort(
      (a, b) => b.zone.priority - a.zone.priority,
    );
    const highestPriorityZone = sorted[0];

    return highestPriorityZone ?? null;
  }
}

export const deliveryAddressStore = new DeliveryAddressStore();
